import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { startOfDay } from 'date-fns';
import { restartableTask, timeout } from 'ember-concurrency';
import sub from 'ember-math-helpers/helpers/sub';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { and, eq, not, gt } from 'ember-truth-helpers';
import EventLogDrawer from 'garaje/components/event-log/event-log-drawer';
import EventLogFilters from 'garaje/components/event-log/event-log-filters';
import MonogramOrPhoto from 'garaje/components/monogram-or-photo';
import SortableHeaderColumn from 'garaje/components/sortable-header-column';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import UiHeader from 'garaje/components/ui/header';
import UiPaginationCombined from 'garaje/components/ui-pagination/combined';
import dateFormat from 'garaje/helpers/date-format';
import highlightText from 'garaje/helpers/highlight-text';
import { type PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
import type EventLogModel from 'garaje/models/event-log';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type SessionService from 'garaje/services/session';
import type StateService from 'garaje/services/state';
import { DEFAULT_TIMEZONE, modifyDateInTimeZone } from 'garaje/utils/date-fns-tz-utilities';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

const DATE_FORMAT = 'MMM d, yyyy';
const TIME_FORMAT = 'h:mm:ss aa zzz';

interface EventLogTableSignature {
  Args: {
    isGlobal?: boolean;
    page: number;
    search: string;
    sort: string;
    locationId?: number;
    productAreas?: string[];
    actors?: string[];
    eventTypes?: string[];
    timezone?: string;
    changePage: (page: number) => void;
    changeSearch: (search: string) => void;
    changeSort: (sort: string) => void;
    changeLocation: (locationId: number | null) => void;
    changeProductAreas: (productAreas: string[]) => void;
    changeActors: (actors: string[]) => void;
    changeEventTypes: (eventTypes: string[]) => void;
  };
}

const PAGE_SIZE = 25;

export default class EventLogTable extends Component<EventLogTableSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare store: Store;
  @service declare session: SessionService;
  @service declare state: StateService;
  @service declare metrics: MetricsService;

  @tracked entries: EventLogModel[] = [];
  @tracked totalCount = 0;
  @tracked pageCount = 0;
  @tracked selectedEvent: EventLogModel | null = null;
  @tracked isDrawerOpen = false;
  @tracked selectedTimezone: string | null = null;
  @tracked dateFrom: Date = new Date();
  @tracked dateTo: Date = new Date();

  constructor(owner: unknown, args: EventLogTableSignature['Args']) {
    super(owner, args);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    this.dateFrom = modifyDateInTimeZone(sevenDaysAgo, this.timezone, startOfDay);
    void this.loadData.perform();
  }

  get isGlobal(): boolean {
    return this.args.isGlobal ?? false;
  }

  get currentPage(): number {
    return this.args.page ?? 1;
  }

  get searchQuery(): string {
    return this.args.search ?? '';
  }

  get sortField(): string {
    const [field = 'event_timestamp'] = (this.args.sort ?? 'event_timestamp:desc').split(':');
    return field;
  }

  get sortDirection(): 'asc' | 'desc' {
    const [, direction] = (this.args.sort ?? 'event_timestamp:desc').split(':');
    return direction === 'asc' ? 'asc' : 'desc';
  }

  get pageTitle(): string {
    if (this.isGlobal) {
      return 'Event log';
    }
    const locationName = this.state.currentLocation?.name;
    return locationName ? `Event log for ${locationName}` : 'Event log';
  }

  get timezone(): string {
    const {
      args: { timezone },
      isGlobal,
      selectedTimezone,
    } = this;

    if (selectedTimezone) return selectedTimezone;
    if (isGlobal) return DEFAULT_TIMEZONE;

    return timezone ?? this.state.currentLocation?.timezone ?? DEFAULT_TIMEZONE;
  }

  loadData = restartableTask(async () => {
    try {
      const companyId = this.session.companyId;
      if (!companyId) {
        throw new Error('No company ID available');
      }

      const companyIdNum = parseInt(companyId, 10);

      const from = (this.currentPage - 1) * PAGE_SIZE;
      const queryParams: Record<string, unknown> = {
        companyId: companyIdNum,
        from,
        size: PAGE_SIZE,
        sort: [`${this.sortField}:${this.sortDirection}`],
      };

      if (this.searchQuery) {
        queryParams['searchText'] = this.searchQuery;
      }

      if (this.dateFrom) {
        // Convert date to timestamp
        queryParams['fromTimestamp'] = this.dateFrom.getTime().toString();
      }

      if (this.dateTo) {
        const endOfTime = new Date(this.dateTo);
        // Set time to include the whole minute
        endOfTime.setSeconds(59);
        endOfTime.setMilliseconds(999);
        queryParams['toTimestamp'] = endOfTime.getTime().toString();
      }

      if (!this.isGlobal && this.state.currentLocation?.id) {
        queryParams['locationId'] = this.state.currentLocation.id;
      } else if (this.isGlobal && this.args.locationId) {
        queryParams['locationId'] = this.args.locationId;
      }

      // Initialize filters array
      const filters: string[] = [];

      if (this.args.productAreas && this.args.productAreas.length > 0) {
        const productAreaValues = this.args.productAreas.join('|');
        filters.push(`product_area:${productAreaValues}`);
      }

      if (this.args.actors && this.args.actors.length > 0) {
        const actorValues = this.args.actors.join('|');
        filters.push(`actor:${actorValues}`);
      }

      if (this.args.eventTypes && this.args.eventTypes.length > 0) {
        const eventTypeValues = this.args.eventTypes.join('|');
        filters.push(`event:${eventTypeValues}`);
      }

      if (filters.length > 0) {
        queryParams['filter'] = filters;
      }

      const result = <PaginatedRecordArray<EventLogModel>>await this.store.query('event-log', queryParams);

      this.entries = result.slice();
      this.totalCount = result.meta?.total || 0;
      this.pageCount = Math.ceil(this.totalCount / PAGE_SIZE);
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', 'Error loading event log data', {
        details: parseErrorForDisplay(error),
      });
      this.entries = [];
      this.totalCount = 0;
      this.pageCount = 0;
    }
  });

  searchTask = restartableTask(async () => {
    await timeout(300); // Debounce search
    void this.loadData.perform();
  });

  @action
  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const searchValue = target.value;

    // Track search queries (only non-empty searches to avoid noise)
    if (searchValue.trim().length > 0) {
      this.metrics.trackEvent('Event Log Search Performed', {
        search_query: searchValue.trim(),
        context: this.isGlobal ? 'global_event_log' : 'local_event_log',
        scope: this.isGlobal ? 'global' : 'location',
      });
    }

    this.args.changeSearch(searchValue);
    void this.searchTask.perform();
  }

  @action
  onSearchClear(): void {
    this.metrics.trackEvent('Event Log Search Cleared', {
      context: this.isGlobal ? 'global_event_log' : 'local_event_log',
      scope: this.isGlobal ? 'global' : 'location',
    });

    this.args.changeSearch('');
    void this.loadData.perform();
  }

  @action
  setPageNumber(page: number): void {
    this.args.changePage(page);
    void this.loadData.perform();
  }

  @action
  sortAction(sortField: string, direction: 'asc' | 'desc'): void {
    const sortValue = `${sortField}:${direction}`;
    this.args.changeSort(sortValue);
    void this.loadData.perform();
  }

  @action
  onLocationChange(locationId: number | null): void {
    this.args.changeLocation(locationId);
    void this.loadData.perform();
  }

  @action
  onProductAreasChange(productAreas: string[]): void {
    this.args.changeProductAreas(productAreas);
    void this.loadData.perform();
  }

  @action
  onActorsChange(actors: string[]): void {
    this.args.changeActors(actors);
    void this.loadData.perform();
  }

  @action
  onEventTypesChange(eventTypes: string[]): void {
    this.args.changeEventTypes(eventTypes);
    void this.loadData.perform();
  }

  @action
  changeTimezone(timezone: string): void {
    this.selectedTimezone = timezone;
  }

  @action
  updateDateRange(startDate: Date, endDate: Date): void {
    this.dateFrom = startDate;
    this.dateTo = endDate;
    this.args.changePage(1); // Reset to first page
    void this.loadData.perform();
  }

  @action
  openDrawer(event: EventLogModel): void {
    this.selectedEvent = event;
    this.isDrawerOpen = true;
  }

  @action
  closeDrawer(): void {
    this.isDrawerOpen = false;
    this.selectedEvent = null;
  }

  get timezoneForEvent(): (model: EventLogModel | null) => string {
    return (model: EventLogModel | null = null): string => {
      // If rendering log for a single location, show tooltip if @timezone is not system timezone
      if (!this.isGlobal) return this.timezone === DEFAULT_TIMEZONE ? '' : DEFAULT_TIMEZONE;
      if (!model?.locationId) return '';

      const location =
        this.store.peekRecord('skinny-location', `${model.locationId}`) ||
        this.store.peekRecord('location', `${model.locationId}`);

      // If rendering a global log, compute timezone based on event location
      return location?.timezone ?? '';
    };
  }

  <template>
    <UiHeader>
      {{this.pageTitle}}
    </UiHeader>

    <EventLogFilters
      @isGlobal={{this.isGlobal}}
      @search={{this.searchQuery}}
      @locationId={{@locationId}}
      @productAreas={{@productAreas}}
      @actors={{@actors}}
      @eventTypes={{@eventTypes}}
      @dateFrom={{this.dateFrom}}
      @dateTo={{this.dateTo}}
      @timezone={{this.timezone}}
      @onSearchInput={{this.onSearchInput}}
      @onSearchClear={{this.onSearchClear}}
      @onLocationChange={{this.onLocationChange}}
      @onProductAreasChange={{this.onProductAreasChange}}
      @onActorsChange={{this.onActorsChange}}
      @onEventTypesChange={{this.onEventTypesChange}}
      @onDateRangeChange={{this.updateDateRange}}
      @onTimezoneChange={{this.changeTimezone}}
    />

    <div class="table table-auto w-full border rounded text-carbon-50" data-test-event-log-table>
      <div class="table-row" data-test-event-log-header>
        <div
          class="table-cell border-r bg-light-grey text-sm p-3 px-5 align-middle font-medium min-w-32 whitespace-nowrap
            {{if (gt this.entries.length 0) 'border-b' ''}}"
        >
          <SortableHeaderColumn
            @name="Date and time"
            @sortField="event_timestamp"
            @sortAction={{this.sortAction}}
            @isActive={{eq this.sortField "event_timestamp"}}
            @sortDirection={{this.sortDirection}}
          />
        </div>
        <div
          class="table-cell border-r bg-light-grey text-sm p-3 px-5 align-middle font-medium min-w-28 whitespace-nowrap
            {{if (gt this.entries.length 0) 'border-b' ''}}"
        >
          Actor
        </div>
        <div
          class="table-cell bg-light-grey text-sm p-3 px-5 align-middle font-medium
            {{if (gt this.entries.length 0) 'border-b' ''}}"
        >
          Event
        </div>
        {{#if this.isGlobal}}
          <div
            class="table-cell border-l bg-light-grey text-sm p-3 px-5 align-middle font-medium
              {{if (gt this.entries.length 0) 'border-b' ''}}"
          >
            Location
          </div>
        {{/if}}
      </div>

      {{#if this.loadData.isRunning}}
        <LoadingSpinnerPulse data-test-loading-spinner class="pt-12" />
      {{else}}
        {{#each this.entries as |entry index|}}
          <div
            class="table-row hover:bg-carbon-5 cursor-pointer
              {{if (eq index (sub this.entries.length 1)) 'last-row' ''}}"
            data-test-event-log-row
            data-event-type="{{entry.eventType}}"
            data-location-id="{{entry.locationId}}"
            {{on "click" (fn this.openDrawer entry)}}
          >
            <div
              class="table-cell border-b p-3 px-5 align-middle text-sm min-w-32 whitespace-nowrap"
              data-test-event-datetime
            >
              {{#if entry.dateTime}}
                <div>
                  <div class="text-carbon-70">
                    {{dateFormat entry.dateTime DATE_FORMAT timezone=this.timezone}}
                  </div>
                  <div class="text-carbon-50">
                    {{dateFormat entry.dateTime TIME_FORMAT timezone=this.timezone}}
                  </div>
                  {{#let (this.timezoneForEvent entry) as |tooltipTimezone|}}
                    {{#if tooltipTimezone}}
                      <EmberTooltip @side="bottom">
                        <div class="p-1">
                          <div>
                            {{dateFormat entry.dateTime DATE_FORMAT timezone=tooltipTimezone}}
                          </div>
                          <div>
                            {{dateFormat entry.dateTime TIME_FORMAT timezone=tooltipTimezone}}
                          </div>
                        </div>
                      </EmberTooltip>
                    {{/if}}
                  {{/let}}
                </div>
              {{else}}
                <span class="text-carbon-50">N/A</span>
              {{/if}}
            </div>
            <div
              class="table-cell border-b p-3 px-5 align-middle text-carbon-70 text-base min-w-28 whitespace-nowrap"
              data-test-event-actor
            >
              {{#if entry.actorName}}
                <div class="flex items-center gap-1">
                  {{#if (eq entry.actorType "system")}}
                    <div class="mx-1 w-10 h-10 bg-carbon-10 rounded-full flex items-center justify-center">
                      <span class="flex" style="padding-left: 0.05rem;">
                        {{svgJar "logo-small-red" width="18" height="18"}}
                      </span>
                    </div>
                  {{else}}
                    <MonogramOrPhoto class="!p-0" @name={{entry.actorName}} @medium={{true}} @borderBottom={{false}} />
                  {{/if}}
                  <span style={{if (eq entry.actorType "system") "padding-left: 0.16rem;"}}>
                    {{highlightText entry.actorName this.searchQuery}}
                  </span>
                </div>
              {{else}}
                -
              {{/if}}
            </div>
            <div
              class="table-cell border-b p-3 px-5 align-middle text-sm text-carbon-50"
              data-test-event-type
              data-test-event-description
            >
              <p class="mb-0 text-carbon-70">
                {{#if entry.eventTypeName}}
                  {{highlightText entry.eventTypeName this.searchQuery}}
                {{else}}
                  -
                {{/if}}
              </p>
              {{#if entry.eventDescription}}
                {{highlightText entry.eventDescription this.searchQuery}}
              {{else}}
                -
              {{/if}}
            </div>
            {{#if this.isGlobal}}
              <div class="table-cell border-b p-3 px-5 align-middle text-sm text-carbon-50" data-test-event-location>
                {{entry.location}}
              </div>
            {{/if}}
          </div>
        {{/each}}
      {{/if}}
    </div>

    {{#if (and (not this.loadData.isRunning) (not this.entries.length))}}
      <div class="flex justify-center items-center py-12" data-test-empty-state>
        <div class="text-center text-base text-carbon-50">
          No events found
        </div>
      </div>
    {{/if}}

    {{#if (and (not this.loadData.isRunning) this.entries.length)}}
      <UiPaginationCombined
        data-test-pagination
        @pageCount={{this.pageCount}}
        @pageNumber={{@page}}
        @pageSize={{PAGE_SIZE}}
        @recordCount={{this.totalCount}}
        @setPageNumber={{this.setPageNumber}}
      />
    {{/if}}

    {{#if this.selectedEvent}}
      <EventLogDrawer
        @event={{this.selectedEvent}}
        @isOpen={{this.isDrawerOpen}}
        @onClose={{this.closeDrawer}}
        @timezone={{this.timezone}}
      />
    {{/if}}
  </template>
}
